import React from 'react'
import { View } from 'react-native'

import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import NetworkIcon from '@common/components/NetworkIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'

import { getFormattedSubmittedDate, getModalFinalStatus, getTruncatedNetworkName } from './helpers'
import getStyles from './styles'

type Props = {
  status?: AccountOpStatus
  chainId: bigint
  networkName?: string
  timestamp: number
  unresolvedAfterNonceSpent?: boolean
  /**
   * RAILGUN ShieldBlocked — a POI-layer state, not an on-chain failure. Overrides
   * the derived label with an amber "Blocked".
   */
  isBlocked?: boolean
}

/**
 * The closing "Confirmed — <date> on <network>" row of the activity detail sheet.
 * Shared by the public and the private detail views so both render the same
 * format — see Trello #467.
 */
const FinalStatusRow = ({
  status,
  chainId,
  networkName,
  timestamp,
  unresolvedAfterNonceSpent = false,
  isBlocked = false
}: Props) => {
  const { styles } = useTheme(getStyles)

  const finalStatus = isBlocked
    ? { label: 'Blocked', appearance: 'warningText' as const }
    : getModalFinalStatus(status, unresolvedAfterNonceSpent)

  if (!finalStatus) return null

  const shouldShowDate = !isBlocked && status === AccountOpStatus.Success

  return (
    <View style={[styles.modalConfirmedRow, spacings.mbSm]} testID="activity-confirmed-step">
      <View style={[styles.modalStepRow, spacings.mbSm]}>
        <Text appearance={finalStatus.appearance} fontSize={16} weight="medium">
          {finalStatus.label}
        </Text>
        {shouldShowDate && (
          <View style={styles.modalStepRowRight}>
            <Text fontSize={14} appearance="secondaryText">
              {getFormattedSubmittedDate(timestamp)} on {getTruncatedNetworkName(networkName)}
            </Text>
            <NetworkIcon id={chainId.toString()} size={20} style={spacings.mlMi} />
          </View>
        )}
      </View>
    </View>
  )
}

export default React.memo(FinalStatusRow)
